/**
 * lib/consent/client.ts — browser side of the consent signal (FRIDGE-115).
 *
 * Reads the middleware-stamped region cookie and the visitor's stored choice
 * synchronously from `document.cookie` (no network round trip, so the
 * decision is known before any tag can run), records a choice, and tells the
 * telemetry initialisers about it through one window event.
 *
 * Callers:
 *   - app/providers.tsx        — PostHog init waits on isTelemetryAllowed()
 *   - sentry.client.config.ts  — Sentry init waits on isTelemetryAllowed()
 *   - app/posthog-pageview.tsx — captures the current page once Allow lands
 *   - components/consent-banner.tsx / consent-choice-control.tsx — the UI
 *
 * Safe to import from server-rendered modules: every function checks for a
 * document first and reports "not gated, undecided" on the server, so SSR
 * output is identical for every region (the HTML stays cacheable).
 */
import {
  CONSENT_DECISION_COOKIE,
  CONSENT_DECISION_MAX_AGE_SECONDS,
  GTAG_CONSENT_DENIED_UPDATE,
  GTAG_CONSENT_GRANTED_UPDATE,
  isTelemetryAllowedFor,
  readConsentState,
  type ConsentDecision,
  type ConsentState,
} from './signal'

/** Window event fired after every recorded choice. `detail` is the decision. */
export const CONSENT_EVENT = 'fk:consent'

type GtagWindow = Window & { gtag?: (...args: unknown[]) => void }

function hasDocument(): boolean {
  return typeof document !== 'undefined' && typeof document.cookie === 'string'
}

/** Current state from the browser cookies (server: not gated, undecided). */
export function getConsentState(): ConsentState {
  if (!hasDocument()) return { gated: false, decision: null }
  return readConsentState(document.cookie)
}

/** True when this visitor's country was marked consent-gated by the middleware. */
export function isConsentGated(): boolean {
  return getConsentState().gated
}

/** The stored choice, or null while undecided. */
export function getConsentDecision(): ConsentDecision | null {
  return getConsentState().decision
}

/**
 * May analytics and error monitoring run right now? Everyone outside the
 * gated regions: yes (unchanged). Gated visitors: only after Allow.
 */
export function isTelemetryAllowed(): boolean {
  return isTelemetryAllowedFor(getConsentState())
}

/** The banner shows only while gated and undecided. */
export function needsConsentPrompt(): boolean {
  const state = getConsentState()
  return state.gated && state.decision === null
}

function writeDecisionCookie(decision: ConsentDecision): void {
  if (!hasDocument()) return
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${CONSENT_DECISION_COOKIE}=${decision}; Max-Age=${CONSENT_DECISION_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`
}

function sendGtagConsentUpdate(update: Record<string, string>): void {
  if (typeof window === 'undefined') return
  const gtag = (window as GtagWindow).gtag
  if (typeof gtag === 'function') gtag('consent', 'update', update)
}

function dispatchDecision(decision: ConsentDecision): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<ConsentDecision>(CONSENT_EVENT, { detail: decision }))
}

/**
 * Record Allow: store the choice, flip Google Consent Mode to granted for
 * analytics storage, and notify the initialisers (PostHog and Sentry start;
 * the current page view is captured).
 */
export function grantConsent(): void {
  writeDecisionCookie('granted')
  sendGtagConsentUpdate({ ...GTAG_CONSENT_GRANTED_UPDATE })
  dispatchDecision('granted')
}

/**
 * Record Decline. When it follows an earlier Allow (withdrawal) the caller
 * should also clear the identifiers and reload — see
 * `clearTelemetryIdentifiers`; the pure "never started" case needs nothing
 * more than the cookie.
 */
export function denyConsent(): void {
  writeDecisionCookie('denied')
  sendGtagConsentUpdate({ ...GTAG_CONSENT_DENIED_UPDATE })
  dispatchDecision('denied')
}

/** Subscribe to recorded choices. Returns the unsubscribe function. */
export function onConsentChange(cb: (decision: ConsentDecision) => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<ConsentDecision>).detail
    if (detail === 'granted' || detail === 'denied') cb(detail)
  }
  window.addEventListener(CONSENT_EVENT, handler)
  return () => window.removeEventListener(CONSENT_EVENT, handler)
}

/** Subscribe to Allow only. Returns the unsubscribe function. */
export function onConsentGranted(cb: () => void): () => void {
  return onConsentChange((decision) => {
    if (decision === 'granted') cb()
  })
}

/** Cookie names the analytics tags write; deleted on withdrawal. */
const TELEMETRY_COOKIE_PATTERN = /^(_ga|_gid|_gat|ph_|fc_acq$)/

/**
 * Best-effort removal of the identifiers the tags wrote while consent stood:
 * GA (`_ga`, `_ga_*`) and PostHog (`ph_*`) cookies on every domain scope
 * they could have been set with, PostHog localStorage keys, and the Sentry
 * replay session key. Used on withdrawal, before a reload; never on the
 * first-visit Decline (nothing was written then).
 */
export function clearTelemetryIdentifiers(): void {
  if (!hasDocument()) return
  try {
    const hostname = typeof location !== 'undefined' ? location.hostname : ''
    const labels = hostname.split('.')
    const domains = new Set<string>([''])
    if (hostname) domains.add(hostname)
    if (labels.length >= 2) domains.add(labels.slice(-2).join('.'))
    for (const part of document.cookie.split(';')) {
      const name = part.slice(0, part.indexOf('=') === -1 ? part.length : part.indexOf('=')).trim()
      if (!name || !TELEMETRY_COOKIE_PATTERN.test(name)) continue
      for (const domain of domains) {
        const domainAttr = domain ? `; Domain=${domain}` : ''
        document.cookie = `${name}=; Max-Age=0; Path=/${domainAttr}`
      }
    }
  } catch {
    // cookie access blocked — nothing to clear
  }
  try {
    const keys: string[] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (key && /^ph_/.test(key)) keys.push(key)
    }
    for (const key of keys) window.localStorage.removeItem(key)
  } catch {
    // storage blocked
  }
  try {
    window.sessionStorage.removeItem('sentryReplaySession')
  } catch {
    // storage blocked
  }
}
